import React from 'react'
import { Route, Routes } from 'react-router-dom'

import { BaseLayout } from 'components'
import Home from 'pages/dashboard/home'
import PageComponent from 'router/page-component'

const Devices = React.lazy(() => import('pages/dashboard/devices'))
const DeviceDetails = React.lazy(() => import('pages/dashboard/devices/device-details'))
const DeviceDetailsConfirmOrganizer = React.lazy(() => import('pages/dashboard/devices/device-details-confirm-organizer'))
const RegisteredDeviceDetails = React.lazy(() => import('pages/dashboard/devices/registered-device-details'))
const DashboardError = React.lazy(() => import('pages/dashboard/error'))
const RegisterPassword = React.lazy(() => import('pages/dashboard/etc/register-password'))
const DashboardIntro = React.lazy(() => import('pages/dashboard/intro'))
const LoginRedirected = React.lazy(() => import('pages/dashboard/login-redirected'))
const Logout = React.lazy(() => import('pages/dashboard/logout'))
const Places = React.lazy(() => import('pages/dashboard/places'))
const Privacy = React.lazy(() => import('pages/dashboard/privacy'))
const ConsignmentAgencies = React.lazy(() => import('pages/dashboard/privacy/consignment-agencies'))
const CustomizationService = React.lazy(() => import('pages/dashboard/privacy/customization-service'))
const ReceiveCustomizationService = React.lazy(() => import('pages/dashboard/privacy/customization-service/receive'))
const WithdrawCustomizationService = React.lazy(() => import('pages/dashboard/privacy/customization-service/withdraw'))
const DetailsCustomizationService = React.lazy(() => import('pages/dashboard/privacy/customization-service/details'))
const MarketingPrivacyNoticeDetail = React.lazy(() => import('pages/dashboard/privacy/marketing-privacy-notice-detail'))
const MarketingNoticeDetail = React.lazy(() => import('pages/dashboard/privacy/marketing-notice-detail'))
const ReceivePersonalizedAdvertising = React.lazy(() => import('pages/dashboard/privacy/personalized/advertising/receive'))
const WithdrawPersonalizedAdvertising = React.lazy(() => import('pages/dashboard/privacy/personalized/advertising/withdraw'))
const PrivacyConfirmation = React.lazy(() => import('pages/dashboard/privacy/metadata/confirmation'))
const Profile = React.lazy(() => import('pages/dashboard/profile'))
const ManageAccount = React.lazy(() => import('pages/dashboard/profile/account'))
const EmailIdView = React.lazy(() => import('pages/dashboard/profile/account/email-id'))
const OtpEmailLimitExceed = React.lazy(() => import('pages/dashboard/profile/account/otp-email/limit-exceed'))
const EmailIdAddedView = React.lazy(() => import('pages/dashboard/profile/account/email-id/added'))
const ChangedEmailId = React.lazy(() => import('pages/dashboard/profile/account/email-id/changed'))
const EmailIdOtpSendingBlocked = React.lazy(() => import('pages/dashboard/profile/account/email-id/error/otp-sending-blocked'))
const EmailIdOtpVerificationBlocked = React.lazy(
  () => import('pages/dashboard/profile/account/email-id/error/otp-verification-blocked')
)
const LinkGoogleAccount = React.lazy(() => import('pages/dashboard/profile/account/link/google'))
const LinkGoogleError = React.lazy(() => import('pages/dashboard/profile/account/link/google/error'))
const UnlinkedGoogle = React.lazy(() => import('pages/dashboard/profile/account/link/google/unlinked'))
const LinkWeChatAccount = React.lazy(() => import('pages/dashboard/profile/account/link/wechat'))
const LinkWeChatError = React.lazy(() => import('pages/dashboard/profile/account/link/wechat/error'))
const UnlinkedWeChat = React.lazy(() => import('pages/dashboard/profile/account/link/wechat/unlinked'))
const LinkKakaoAccount = React.lazy(() => import('pages/dashboard/profile/account/link/kakao'))
const LinkKakaoAccountRedirect = React.lazy(() => import('pages/dashboard/profile/account/link/kakao/redirect'))
const LinkKakaoError = React.lazy(() => import('pages/dashboard/profile/account/link/kakao/error'))
const UnlinkedKakao = React.lazy(() => import('pages/dashboard/profile/account/link/kakao/unlinked'))
const PhoneIdView = React.lazy(() => import('pages/dashboard/profile/account/phone-id'))
const Withdraw = React.lazy(() => import('pages/dashboard/profile/account/withdraw'))
const WithdrawConfirmOrganizer = React.lazy(() => import('pages/dashboard/profile/account/withdraw/confirm-organizer'))
const WithdrawResult = React.lazy(() => import('pages/dashboard/profile/account/withdraw/result'))
const PersonalInformation = React.lazy(() => import('pages/dashboard/profile/information'))
const ProfileInformationPhone = React.lazy(() => import('pages/dashboard/profile/information/phone/phone'))
const PhoneNumberConfirm = React.lazy(() => import('pages/dashboard/profile/information/phone/phone-number-confirm'))
const SecurityPhone = React.lazy(() => import('pages/dashboard/security/phone'))
const Security = React.lazy(() => import('pages/dashboard/security'))
const PasswordCompromised = React.lazy(() => import('pages/dashboard/security/password-compromised'))
const OrganizerPasswordCompromised = React.lazy(() => import('pages/dashboard/security/organizer-password-compromised'))
const Activities = React.lazy(() => import('pages/dashboard/security/activities'))
const SecurityRecovery = React.lazy(() => import('pages/dashboard/security/recovery'))
const RecoveryOtpSendingBlocked = React.lazy(() => import('pages/dashboard/security/recovery/email/error/otp-sending-blocked'))
const RecoveryOtpVerificationBlocked = React.lazy(
  () => import('pages/dashboard/security/recovery/email/error/otp-verification-blocked')
)
const SecurityRecoveryEmail = React.lazy(() => import('pages/dashboard/security/recovery/recovery-email'))
const SecurityRecoveryPhoneNumber = React.lazy(() => import('pages/dashboard/security/recovery/recovery-phone-number'))
const SecurityRecoverySecurityQuestion = React.lazy(() => import('pages/dashboard/security/recovery/recovery-security-question'))
const ResetPassword = React.lazy(() => import('pages/dashboard/security/reset-password'))
const ResetPasswordConfirmOrganizer = React.lazy(() => import('pages/dashboard/security/reset-password/confirm-organizer'))
const Changed = React.lazy(() => import('pages/dashboard/security/reset-password/changed/changed'))
const TwoStepVerification = React.lazy(() => import('pages/dashboard/security/two-step-verification'))
const AuthenticatorAppVerification = React.lazy(() => import('pages/dashboard/security/two-step-verification/authenticator-app'))
const BackupCodeVerification = React.lazy(() => import('pages/dashboard/security/two-step-verification/backup-code'))
const TurnedOn = React.lazy(() => import('pages/dashboard/security/two-step-verification/turned-on/turned-on'))
const PushDeviceVerification = React.lazy(() => import('pages/dashboard/security/two-step-verification/push-device'))
const SmsVerification = React.lazy(() => import('pages/dashboard/security/two-step-verification/sms'))
const TrustedDeviceVerification = React.lazy(() => import('pages/dashboard/security/two-step-verification/trusted-device'))
const Services = React.lazy(() => import('pages/dashboard/services'))
const LinkedServiceDetails = React.lazy(() => import('pages/dashboard/services/linked-service-details'))
const Scloud = React.lazy(() => import('pages/dashboard/services/scloud'))
const ChildGuard = React.lazy(() => import('router/guards/child-guard'))
const IntroGuard = React.lazy(() => import('router/guards/intro-guard'))
const LoggedinGuard = React.lazy(() => import('router/guards/loggedin-guard'))
const UserActiveStatusGuard = React.lazy(() => import('router/guards/user-active-status-guard'))
const SecurityMenuGuard = React.lazy(() => import('router/guards/security-menu-guard'))
const TwoStepVerificationGuard = React.lazy(() => import('router/guards/two-step-verfication-guard'))
const PasswordLeakGuard = React.lazy(() => import('router/guards/password-leak-guard'))
const OrganizerPasswordLeakGuard = React.lazy(() => import('router/guards/organizer-password-leak-guard'))
const ChangeCountry = React.lazy(() => import('pages/dashboard/profile/information/country'))
const ChangeCountryAddEmail = React.lazy(() => import('pages/dashboard/profile/information/country/add-email'))
const ChangeCountryConfirmation = React.lazy(() => import('pages/dashboard/profile/information/country/confirm'))
const ChangeCountryNotice = React.lazy(() => import('pages/dashboard/profile/information/country/notice'))
const ChangeCountryDeletePhonenumbers = React.lazy(
  () => import('pages/dashboard/profile/information/country/delete-old-phonenumbers')
)
const ChangeCountryUnavailable = React.lazy(() => import('pages/dashboard/profile/information/country/unavailable'))
const ChangeCountryGuard = React.lazy(() => import('router/guards/change-country-guard'))
const Family = React.lazy(() => import('pages/dashboard/family'))
const FamilyCreateRedirected = React.lazy(() =>
  import('pages/dashboard/family/create').then((module) => ({ default: module.FamilyCreateRedirected }))
)
const FamilyCreated = React.lazy(() =>
  import('pages/dashboard/family/create').then((module) => ({ default: module.FamilyCreated }))
)
const FamilyManagementGuard = React.lazy(() => import('router/guards/family-management-guard'))
const MemberDetail = React.lazy(() => import('pages/dashboard/family/member-detail'))
const ApplicationList = React.lazy(() =>
  import('pages/dashboard/family/parental-control').then((module) => ({ default: module.ApplicationList }))
)
const AllowApplication = React.lazy(() =>
  import('pages/dashboard/family/parental-control').then((module) => ({ default: module.AllowApplication }))
)
const BlockApplication = React.lazy(() =>
  import('pages/dashboard/family/parental-control').then((module) => ({ default: module.BlockApplication }))
)
const LeaveGroup = React.lazy(() =>
  import('pages/dashboard/family/family-management').then((module) => ({ default: module.LeaveGroup }))
)
const RemoveMember = React.lazy(() =>
  import('pages/dashboard/family/family-management').then((module) => ({ default: module.RemoveMember }))
)
const DeleteFamily = React.lazy(() =>
  import('pages/dashboard/family/family-management').then((module) => ({ default: module.DeleteFamily }))
)
const FamilyDeletionError = React.lazy(() =>
  import('pages/dashboard/family/family-management').then((module) => ({ default: module.FamilyDeletionError }))
)
const FamilyCreationError = React.lazy(() =>
  import('pages/dashboard/family/family-management').then((module) => ({ default: module.FamilyCreationError }))
)
const FamilyCreationErrorMinorRestriction = React.lazy(() =>
  import('pages/dashboard/family/family-management').then((module) => ({ default: module.FamilyCreationErrorMinorRestriction }))
)
const ChildDeviceManagment = React.lazy(() => import('pages/dashboard/family/child-devices-management'))
const FamilyAddMembers = React.lazy(() =>
  import('pages/dashboard/family/family-invitation').then((module) => ({ default: module.AddMember }))
)
const FamilyMemberInvitationsEmail = React.lazy(() =>
  import('pages/dashboard/family/family-invitation').then((module) => ({ default: module.InvitationsEmail }))
)
const FamilyMemberInvitationsQR = React.lazy(() =>
  import('pages/dashboard/family/family-invitation').then((module) => ({ default: module.InvitationsQR }))
)
const FamilyMemberInvitationsSuccess = React.lazy(() =>
  import('pages/dashboard/family/family-invitation').then((module) => ({ default: module.InvitationsSuccess }))
)
const FamilyMemberInvitationFailure = React.lazy(() =>
  import('pages/dashboard/family/family-invitation/invitations-failure').then((module) => ({
    default: module.InvitationsFaulire
  }))
)
const FamilyChildAccountEmailFailure = React.lazy(() =>
  import('pages/dashboard/family/family-invitation/child-failed-email').then((module) => ({
    default: module.ChildAccountEmailFailure
  }))
)
const FamilyChildAccountPasswordFailure = React.lazy(() =>
  import('pages/dashboard/family/family-invitation/child-failed-password').then((module) => ({
    default: module.ChildAccountPasswordFailure
  }))
)
const FamilyChildAccountPhoneFailure = React.lazy(() =>
  import('pages/dashboard/family/family-invitation/child-failed-phone').then((module) => ({
    default: module.ChildAccountPhoneFailure
  }))
)
const FamilyInvitationsAddEmail = React.lazy(() =>
  import('pages/dashboard/family/family-invitation/invite-add-email').then((module) => ({
    default: module.InvitationsAddEmail
  }))
)
const FamilyInvitationsAddPhone = React.lazy(() =>
  import('pages/dashboard/family/family-invitation/invite-add-phone').then((module) => ({
    default: module.InvitationsAddPhone
  }))
)
const FamilyInvitationsAddPassword = React.lazy(() =>
  import('pages/dashboard/family/family-invitation/invite-add-password').then((module) => ({
    default: module.InvitationsAddPassword
  }))
)
const ManagePasskeys = React.lazy(() => import('pages/dashboard/security/passkeys'))
const PasskeyDetails = React.lazy(() => import('pages/dashboard/security/passkeys/detail'))
const PasskeyCreated = React.lazy(() => import('pages/dashboard/security/passkeys/created'))
const PasskeyGuide = React.lazy(() => import('pages/dashboard/security/passkeys/guide'))
const PasskeyUnavailable = React.lazy(() =>
  import('pages/dashboard/security/passkeys/error').then((module) => ({
    default: module.PasskeyUnavailable
  }))
)
const PasskeyError = React.lazy(() =>
  import('pages/dashboard/security/passkeys/error').then((module) => ({
    default: module.PasskeyCreationError
  }))
)
const PasskeyAuthenticatorBlocked = React.lazy(() =>
  import('pages/dashboard/security/passkeys/error').then((module) => ({
    default: module.AuthenticatorBlocked
  }))
)

export default function DashboardRoutes() {
  return (
    <Routes>
      <Route path='/authorize/redirect' element={<LoginRedirected />} />

      <Route element={<BaseLayout />}>
        <Route path='*' element={<PageComponent component={<DashboardError />} name='DashboardError' />} />
        <Route
          path='/profile/account/withdraw/result'
          element={<PageComponent component={<WithdrawResult />} name='WithdrawResult' />}
        />
        <Route element={<IntroGuard />}>
          <Route path='/intro' element={<PageComponent component={<DashboardIntro />} name='DashboardIntro' />} />
          <Route path='/intro/invitation' element={<PageComponent component={<DashboardIntro />} name='InvitationIntro' />} />
        </Route>
        <Route path='/family-intro' element={<PageComponent component={<DashboardIntro />} name='FamilyIntro' />} />
        <Route element={<LoggedinGuard />}>
          <Route path='/logout' element={<PageComponent component={<Logout />} name='Logout' />} />
          <Route path='/' element={<PageComponent component={<Home />} name='Home' />} />
          <Route element={<UserActiveStatusGuard />}>
            <Route path='/profile' element={<PageComponent component={<Profile />} name='Profile' />} />
            <Route path='/profile/account' element={<PageComponent component={<ManageAccount />} name='ManageAccount' />} />
            <Route path='/profile/account/email-id' element={<PageComponent component={<EmailIdView />} name='EmailIdView' />} />
            <Route
              path='/profile/account/email-id/otp-sending-blocked'
              element={<PageComponent component={<EmailIdOtpSendingBlocked />} name='EmailIdOtpSendingBlocked' />}
            />
            <Route
              path='/profile/account/link/google'
              element={<PageComponent component={<LinkGoogleAccount />} name='LinkGoogleAccount' />}
            />
            <Route
              path='/profile/account/link/google/error'
              element={<PageComponent component={<LinkGoogleError />} name='LinkGoogleError' />}
            />
            <Route
              path='/profile/account/link/google/unlinked'
              element={<PageComponent component={<UnlinkedGoogle />} name='UnlinkedGoogle' />}
            />
            <Route
              path='/profile/account/link/wechat'
              element={<PageComponent component={<LinkWeChatAccount />} name='LinkWeChatAccount' />}
            />
            <Route
              path='/profile/account/link/wechat/error'
              element={<PageComponent component={<LinkWeChatError />} name='LinkWeChatError' />}
            />
            <Route
              path='/profile/account/link/wechat/unlinked'
              element={<PageComponent component={<UnlinkedWeChat />} name='UnlinkedWeChat' />}
            />
            <Route
              path='/profile/account/link/kakao'
              element={<PageComponent component={<LinkKakaoAccount />} name='LinkKakaoAccount' />}
            />
            <Route
              path='/profile/account/link/kakao/redirect'
              element={<PageComponent component={<LinkKakaoAccountRedirect />} name='LinkKakaoAccount' />}
            />
            <Route
              path='/profile/account/link/kakao/error'
              element={<PageComponent component={<LinkKakaoError />} name='LinkKakaoError' />}
            />
            <Route
              path='/profile/account/link/kakao/unlinked'
              element={<PageComponent component={<UnlinkedKakao />} name='UnlinkedKakao' />}
            />
            <Route
              path='/profile/account/email-id/changed'
              element={<PageComponent component={<ChangedEmailId />} name='ChangedEmailId' />}
            />
            <Route
              path='/profile/account/email-id/added'
              element={<PageComponent component={<EmailIdAddedView />} name='EmailIdAddedView' />}
            />
            <Route
              path='/profile/account/email-id/otp-verification-blocked'
              element={<PageComponent component={<EmailIdOtpVerificationBlocked />} name='EmailIdOtpVerificationBlocked' />}
            />
            <Route path='/profile/account/phone-id' element={<PageComponent component={<PhoneIdView />} name='PhoneIdView' />} />
            <Route path='/profile/account/withdraw' element={<PageComponent component={<Withdraw />} name='Withdraw' />} />
            <Route
              path='/profile/account/withdraw/confirm-organizer'
              element={<PageComponent component={<WithdrawConfirmOrganizer />} name='WithdrawConfirmOrganizer' />}
            />
            <Route
              path='/profile/information'
              element={<PageComponent component={<PersonalInformation />} name='PersonalInformation' />}
            />
            <Route
              path='/profile/information/phone'
              element={<PageComponent component={<ProfileInformationPhone />} name='ProfileInformationPhone' />}
            />
            <Route
              path='/profile/information/phone/confirm'
              element={<PageComponent component={<PhoneNumberConfirm />} name='PhoneNumberConfirm' />}
            />
            <Route element={<ChangeCountryGuard />}>
              <Route
                path='/profile/information/country'
                element={<PageComponent component={<ChangeCountry />} name='ChangeCountry' />}
              />
              <Route
                path='/profile/information/country/notice'
                element={<PageComponent component={<ChangeCountryNotice />} name='ChangeCountryNotice' />}
              />
              <Route
                path='/profile/information/country/delete-phone-numbers'
                element={<PageComponent component={<ChangeCountryDeletePhonenumbers />} name='ChangeCountryDeletePhonenumbers' />}
              />
              <Route
                path='/profile/information/country/confirm'
                element={<PageComponent component={<ChangeCountryConfirmation />} name='ChangeCountryConfirmation' />}
              />
              <Route
                path='/profile/information/country/add-email'
                element={<PageComponent component={<ChangeCountryAddEmail />} name='ChangeCountryAddEmail' />}
              />
              <Route
                path='/profile/information/country/unavailable'
                element={<PageComponent component={<ChangeCountryUnavailable />} name='ChangeCountryUnavailable' />}
              />
            </Route>
            <Route element={<SecurityMenuGuard />}>
              <Route
                path='/security/password-compromised'
                element={<PageComponent component={<PasswordCompromised />} name='PasswordCompromised' />}
              />
              <Route element={<ChildGuard />}>
                <Route
                  path='/security/organizer-password-compromised'
                  element={<PageComponent component={<OrganizerPasswordCompromised />} name='OrganizerPasswordCompromised' />}
                />
                <Route element={<PasswordLeakGuard />}>
                  <Route element={<OrganizerPasswordLeakGuard />}>
                    <Route path='/security' element={<PageComponent component={<Security />} name='Security' />} />
                    <Route
                      path='/security/phone'
                      element={<PageComponent component={<SecurityPhone />} name='SecurityPhone' />}
                    />
                    <Route
                      path='/security/phone/confirm'
                      element={<PageComponent component={<PhoneNumberConfirm />} name='PhoneNumberConfirm' />}
                    />
                    <Route path='/security/activities' element={<PageComponent component={<Activities />} name='Activities' />} />
                    <Route element={<TwoStepVerificationGuard />}>
                      <Route
                        path='/security/two-step-verification'
                        element={<PageComponent component={<TwoStepVerification />} name='TwoStepVerification' />}
                      />
                      <Route
                        path='/security/two-step-verification/authenticator-app'
                        element={
                          <PageComponent component={<AuthenticatorAppVerification />} name='AuthenticatorAppVerification' />
                        }
                      />
                      <Route
                        path='/security/two-step-verification/sms'
                        element={<PageComponent component={<SmsVerification />} name='SmsVerification' />}
                      />
                      <Route
                        path='/security/two-step-verification/push-device'
                        element={<PageComponent component={<PushDeviceVerification />} name='PushDeviceVerification' />}
                      />
                      <Route
                        path='/security/two-step-verification/backup-code'
                        element={<PageComponent component={<BackupCodeVerification />} name='BackupCodeVerification' />}
                      />
                      <Route
                        path='/security/two-step-verification/trusted-device'
                        element={<PageComponent component={<TrustedDeviceVerification />} name='TrustedDeviceVerification' />}
                      />
                    </Route>
                    <Route
                      path='/security/two-step-verification/turned-on'
                      element={<PageComponent component={<TurnedOn />} name='TurnedOn' />}
                    />
                    <Route
                      path='/security/recovery'
                      element={<PageComponent component={<SecurityRecovery />} name='SecurityRecovery' />}
                    />
                    <Route
                      path='/security/recovery/email'
                      element={<PageComponent component={<SecurityRecoveryEmail />} name='SecurityRecoveryEmail' />}
                    />
                    <Route
                      path='/security/recovery/email/error/blocked'
                      element={<PageComponent component={<RecoveryOtpSendingBlocked />} name='RecoveryOtpSendingBlocked' />}
                    />
                    <Route
                      path='/security/recovery/email/error/code-blocked'
                      element={
                        <PageComponent component={<RecoveryOtpVerificationBlocked />} name='RecoveryOtpVerificationBlocked' />
                      }
                    />
                    <Route
                      path='/security/recovery/phonenumber'
                      element={<PageComponent component={<SecurityRecoveryPhoneNumber />} name='SecurityRecoveryPhoneNumber' />}
                    />
                    <Route
                      path='/security/reset-password'
                      element={<PageComponent component={<ResetPassword />} name='ResetPassword' />}
                    />
                    <Route
                      path='/security/reset-password/changed'
                      element={<PageComponent component={<Changed />} name='Changed' />}
                    />
                    <Route
                      path='/security/recovery/security-question'
                      element={
                        <PageComponent component={<SecurityRecoverySecurityQuestion />} name='SecurityRecoverySecurityQuestion' />
                      }
                    />
                    <Route path='/security/passkeys' element={<ManagePasskeys />} />
                    <Route
                      path='/security/passkeys/created'
                      element={<PageComponent component={<PasskeyCreated />} name='PasskeyCreated' />}
                    />
                    <Route
                      path='/security/passkeys/:passkeyId'
                      element={<PageComponent component={<PasskeyDetails />} name='PasskeyDetails' />}
                    />
                    <Route path='/security/passkeys/guide' element={<PasskeyGuide />} />
                    <Route path='/security/passkeys/unavailable' element={<PasskeyUnavailable />} />
                    <Route path='/security/passkeys/blocked' element={<PasskeyAuthenticatorBlocked />} />
                    <Route path='/security/passkeys/error' element={<PasskeyError />} />
                  </Route>
                </Route>
              </Route>
              <Route path='/security/reset-password/confirm-organizer' element={<ResetPasswordConfirmOrganizer />} />
            </Route>
          </Route>

          <Route path='/family' element={<PageComponent component={<Family />} name='Family' />} />
          <Route
            path='/family/create'
            element={<PageComponent component={<FamilyCreateRedirected />} name='FamilyCreateRedirected' />}
          />
          <Route path='/family/created' element={<PageComponent component={<FamilyCreated />} name='FamilyCreated' />} />
          <Route
            path='/family/error'
            element={<PageComponent component={<FamilyCreationError />} name='FamilyCreationError' />}
          />
          <Route
            path='/family/error/minor-restriction'
            element={
              <PageComponent component={<FamilyCreationErrorMinorRestriction />} name='FamilyCreationErrorMinorRestriction' />
            }
          />
          <Route element={<FamilyManagementGuard />}>
            <Route
              path='/family/member/:memberKey'
              element={<PageComponent component={<MemberDetail />} name='MemberDetail' />}
            />
            <Route
              path='/family/member/:memberKey/applications'
              element={<PageComponent component={<ApplicationList />} name='ApplicationList' />}
            />
            <Route
              path='/family/member/:memberKey/devices'
              element={<PageComponent component={<ChildDeviceManagment />} name='ChildDeviceManagment' />}
            />
            <Route
              path='/family/member/:memberKey/applications/:applicationId/allow'
              element={<PageComponent component={<AllowApplication />} name='AllowApplication' />}
            />
            <Route
              path='/family/member/:memberKey/applications/:applicationId/block'
              element={<PageComponent component={<BlockApplication />} name='BlockApplication' />}
            />
            <Route
              path='/family/member/:memberKey/leave'
              element={<PageComponent component={<LeaveGroup />} name='LeaveGroup' />}
            />
            <Route
              path='/family/member/:memberKey/remove'
              element={<PageComponent component={<RemoveMember />} name='RemoveMember' />}
            />
            <Route
              path='/family/member/:memberKey/delete'
              element={<PageComponent component={<DeleteFamily />} name='DeleteFamily' />}
            />
            <Route
              path='/family/member/:memberKey/delete/error'
              element={<PageComponent component={<FamilyDeletionError />} name='FamilyDeletionError' />}
            />
            <Route
              path='/family/members/add'
              element={<PageComponent component={<FamilyAddMembers />} name='FamilyAddMembers' />}
            />
            <Route
              path='/family/members/invitations/email'
              element={<PageComponent component={<FamilyMemberInvitationsEmail />} name='FamilyMemberInvitationsEmail' />}
            />
            <Route
              path='/family/members/invitations/qr'
              element={<PageComponent component={<FamilyMemberInvitationsQR />} name='FamilyMemberInvitationsQR' />}
            />
            <Route
              path='/family/members/invitations/success'
              element={<PageComponent component={<FamilyMemberInvitationsSuccess />} name='FamilyMemberInvitationsSuccess' />}
            />
            <Route
              path='/family/members/invitations/failure'
              element={<PageComponent component={<FamilyMemberInvitationFailure />} name='FamilyMemberInvitationFailure' />}
            />
            <Route
              path='/family/members/create-child/email'
              element={<PageComponent component={<FamilyChildAccountEmailFailure />} name='FamilyChildAccountEmailFailure' />}
            />
            <Route
              path='/family/members/create-child/password'
              element={
                <PageComponent component={<FamilyChildAccountPasswordFailure />} name='FamilyChildAccountPasswordFailure' />
              }
            />
            <Route
              path='/family/members/create-child/phone'
              element={<PageComponent component={<FamilyChildAccountPhoneFailure />} name='FamilyChildAccountPhoneFailure' />}
            />
            <Route
              path='/family/members/invitations/add-email'
              element={<PageComponent component={<FamilyInvitationsAddEmail />} name='FamilyInvitationsAddEmail' />}
            />
            <Route
              path='/family/members/invitations/add-phone'
              element={<PageComponent component={<FamilyInvitationsAddPhone />} name='FamilyInvitationsAddPhone' />}
            />
            <Route
              path='/family/members/invitations/add-password'
              element={<PageComponent component={<FamilyInvitationsAddPassword />} name='FamilyInvitationsAddPassword' />}
            />
          </Route>

          <Route path='/privacy' element={<PageComponent component={<Privacy />} name='Privacy' />} />
          <Route
            path='/privacy/consignment-agencies'
            element={<PageComponent component={<ConsignmentAgencies />} name='ConsignmentAgencies' />}
          />
          <Route
            path='/privacy/marketing-privacy-notice-detail'
            element={<PageComponent component={<MarketingPrivacyNoticeDetail />} name='MarketingPrivacyNoticeDetail' />}
          />
          <Route
            path='/privacy/marketing-notice-details'
            element={<PageComponent component={<MarketingNoticeDetail />} name='MarketingNoticeDetail' />}
          />

          <Route
            path='/privacy/customization-service'
            element={<PageComponent component={<CustomizationService />} name='CustomizationService' />}
          />
          <Route
            path='/privacy/customization-service/receive'
            element={<PageComponent component={<ReceiveCustomizationService />} name='ReceiveCustomizationService' />}
          />
          <Route
            path='/privacy/customization-service/withdraw'
            element={<PageComponent component={<WithdrawCustomizationService />} name='WithdrawCustomizationService' />}
          />
          <Route
            path='/privacy/customization-service/details'
            element={<PageComponent component={<DetailsCustomizationService />} name='DetailsCustomizationService' />}
          />
          <Route
            path='/privacy/personalized/advertising/receive'
            element={<PageComponent component={<ReceivePersonalizedAdvertising />} name='ReceivePersonalizedAdvertising' />}
          />
          <Route
            path='/privacy/personalized/advertising/withdraw'
            element={<PageComponent component={<WithdrawPersonalizedAdvertising />} name='WithdrawPersonalizedAdvertising' />}
          />
          <Route path='/privacy/confirmation' element={<PrivacyConfirmation />} />
          <Route path='/places' element={<PageComponent component={<Places />} name='Places' />} />
          <Route path='/services' element={<PageComponent component={<Services />} name='Services' />} />
          <Route
            path='/services/linked/:applicationId'
            element={<PageComponent component={<LinkedServiceDetails />} name='LinkedServiceDetails' />}
          />
          <Route path='/services/scloud' element={<PageComponent component={<Scloud />} name='Scloud' />} />
          <Route path='/devices' element={<PageComponent component={<Devices />} name='Devices' />} />
          <Route
            path='/devices/registered/:serialNumber'
            element={<PageComponent component={<RegisteredDeviceDetails />} name='RegisteredDeviceDetails' />}
          />
          <Route path='/devices/:deviceId' element={<PageComponent component={<DeviceDetails />} name='DeviceDetails' />} />
          <Route path='/register-password' element={<PageComponent component={<RegisterPassword />} name='RegisterPassword' />} />

          <Route path='/family' element={<Family />} />
          <Route path='/family/error' element={<FamilyCreationError />} />
          <Route element={<FamilyManagementGuard />}>
            <Route path='/family/member/:memberKey' element={<MemberDetail />} />
            <Route path='/family/member/:memberKey/applications' element={<ApplicationList />} />
            <Route path='/family/member/:memberKey/devices' element={<ChildDeviceManagment />} />
            <Route path='/family/member/:memberKey/applications/:applicationId/allow' element={<AllowApplication />} />
            <Route path='/family/member/:memberKey/applications/:applicationId/block' element={<BlockApplication />} />
            <Route path='/family/member/:memberKey/leave' element={<LeaveGroup />} />
            <Route path='/family/member/:memberKey/remove' element={<RemoveMember />} />
            <Route path='/family/member/:memberKey/delete' element={<DeleteFamily />} />
            <Route path='/family/member/:memberKey/delete/error' element={<FamilyDeletionError />} />
            <Route path='/family/members/add' element={<FamilyAddMembers />} />
            <Route path='/family/members/invitations/email' element={<FamilyMemberInvitationsEmail />} />
            <Route path='/family/members/invitations/qr' element={<FamilyMemberInvitationsQR />} />
            <Route path='/family/members/invitations/success' element={<FamilyMemberInvitationsSuccess />} />
            <Route path='/family/members/invitations/failure' element={<FamilyMemberInvitationFailure />} />
            <Route path='/family/members/create-child/email' element={<FamilyChildAccountEmailFailure />} />
            <Route path='/family/members/create-child/password' element={<FamilyChildAccountPasswordFailure />} />
            <Route path='/family/members/create-child/phone' element={<FamilyChildAccountPhoneFailure />} />
            <Route path='/family/members/invitations/add-email' element={<FamilyInvitationsAddEmail />} />
            <Route path='/family/members/invitations/add-phone' element={<FamilyInvitationsAddPhone />} />
            <Route path='/family/members/invitations/add-password' element={<FamilyInvitationsAddPassword />} />
          </Route>

          <Route path='/privacy' element={<Privacy />} />
          <Route path='/privacy/consignment-agencies' element={<ConsignmentAgencies />} />
          <Route path='/privacy/marketing-privacy-notice-detail' element={<MarketingPrivacyNoticeDetail />} />
          <Route path='/privacy/marketing-notice-detail' element={<MarketingNoticeDetail />} />

          <Route path='/privacy/customization-service' element={<CustomizationService />} />
          <Route path='/privacy/customization-service/receive' element={<ReceiveCustomizationService />} />
          <Route path='/privacy/customization-service/withdraw' element={<WithdrawCustomizationService />} />
          <Route path='/privacy/customization-service/details' element={<DetailsCustomizationService />} />
          <Route path='/privacy/personalized/advertising/receive' element={<ReceivePersonalizedAdvertising />} />
          <Route path='/privacy/personalized/advertising/withdraw' element={<WithdrawPersonalizedAdvertising />} />
          <Route path='/places' element={<Places />} />
          <Route path='/services' element={<Services />} />
          <Route path='/services/linked/:applicationId' element={<LinkedServiceDetails />} />
          <Route path='/services/scloud' element={<Scloud />} />
          <Route path='/devices' element={<Devices />} />
          <Route path='/devices/registered/:serialNumber' element={<RegisteredDeviceDetails />} />
          <Route path='/devices/:deviceId' element={<DeviceDetails />} />
          <Route path='/devices/:deviceId/confirm-organizer' element={<DeviceDetailsConfirmOrganizer />} />
          <Route path='/register-password' element={<RegisterPassword />} />
          <Route
            path=':prefix/otp-email/limit-exceed'
            element={<PageComponent component={<OtpEmailLimitExceed />} name='OtpEmailLimitExceed' />}
          />
        </Route>
      </Route>
    </Routes>
  )
}
